import React, { memo, useState, useEffect } from 'react';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { Chip, CircularProgress } from '@mui/material';
import { DynamicField } from '@scale/shared/corp/constants';

const useStyles = makeStyles({
  chipElement: {
    margin: '3px !important',
  },
  chipsContainer: {
    padding: 5,
  },
});

interface DynamicChipListProps {
  onChange: (newValue: any) => void;
  value: any;
  apiFetchEndpoint: string;
  transformResult?: (a: any) => DynamicField[];
}

const DynamicChipList = ({ apiFetchEndpoint, transformResult, onChange }: DynamicChipListProps) => {
  const classes = useStyles();
  const [chipList, setChipList] = useState<{ label: string; value: string }[] | undefined>(
    undefined,
  );
  const [toRemoveList, setToRemoveList] = useState<string[]>([]);

  const handleChipClick = (chip: { label: string; value: string }) => {
    const newList = toRemoveList.includes(chip.value)
      ? toRemoveList.filter((value: string) => value !== chip.value)
      : [...toRemoveList, chip.value];
    setToRemoveList(newList);
    onChange(newList);
  };

  useEffect(() => {
    setChipList(undefined);
    setToRemoveList([]);
    const fetchStorageRegions = async () => {
      const fetchResult = await fetchFields('', apiFetchEndpoint, transformResult);
      const realData = fetchResult;
      setChipList(realData);
    };
    fetchStorageRegions();
  }, [apiFetchEndpoint, transformResult]);

  return !chipList ? (
    <CircularProgress />
  ) : (
    <div className={classes.chipsContainer}>
      {chipList!.map((chip: { label: string; value: string }) => (
        <Chip
          onClick={() => handleChipClick(chip)}
          clickable={true}
          className={classes.chipElement}
          key={chip.value}
          color={toRemoveList.includes(chip.value) ? 'error' : 'success'}
          label={chip.label}
        />
      ))}
    </div>
  );
};

export default memo(DynamicChipList);

async function fetchFields(search: string, apiFetchEndpoint: string, transformResult?: any) {
  try {
    const response =
      (await apiFetch(null, apiFetchEndpoint, {
        method: 'GET',
        query: { search },
      })) ?? [];
    const results = transformResult ? transformResult(response) : response;
    const options = results.map((oneField: DynamicField) => ({
      label: oneField.label,
      value: oneField.id,
    }));
    return options;
  } catch (error) {
    defaultApiFetchErrorHandler(error, { handleHttp: true });
    return [];
  }
}
